import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Radio } from 'src/design/atoms/Radio';
import { Tabs } from 'src/design/molecules/Tabs';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { IconAlertCircle } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import {
  DivisionDto,
  JobClassificationRateRequestDto,
  JobDto,
  JobPayCodeRequestDto,
} from 'src/api/FiretrolApiClient';
import {
  useSyncCtcJobMetadata,
  useUpdateJobDetails,
} from 'src/api/queries/jobs/useJobMutations';
import { useOrganization } from 'src/hooks/useOrganization';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { ClassificationRatesOverride } from 'src/app/dialogs/CreateJobDialog/ClassificationRatesOverride';
import { PayCodeOverrides } from 'src/app/dialogs/CreateJobDialog/PayCodeOverrides';
import { ClientSelector } from 'src/app/selectors/ClientSelector';
import { PlaceSelector } from 'src/app/selectors/PlaceSelector';
import { getErrorMessage } from 'src/utils';

interface EditDetailsProps {
  job: JobDto;
  onCancel: () => void;
  onSave: () => void;
}

export const EditDetails: React.FC<EditDetailsProps> = ({
  job,
  onCancel,
  onSave,
}) => {
  const { divisions } = useOrganization();
  const updateDetails = useUpdateJobDetails();
  const syncCtc = useSyncCtcJobMetadata();
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<string | null>('basic');
  const isServiceJob = job.jobType === 'service';
  const [values, setValues] = React.useState({
    name: job.name,
    description: job.description,
    job_number: job.jobNumber,
    customer_name: job.customerName || '',
    customer_number: job.customerNumber || '',
    location: job.location,
    division_id: job.divisionId,
    tax_exempt: job.taxExempt,
    vendor_discounts: job.vendorDiscounts,
    per_diem_enabled: job.perDiemEnabled,
    per_diem_amount: job.perDiemAmount,
    scale_pay_enabled: job.scalePayEnabled,
    pay_code_overrides: (job.payCodeOverrides || []).map((o) => ({
      payCodeId: o.payCodeId,
      rateModifierMultiplier: o.rateModifierMultiplier,
    })) as Omit<JobPayCodeRequestDto, 'jobId'>[],
    classification_rates: (job.classificationRates || []).map((r) => ({
      classificationId: r.classificationId,
      scaleRate: r.scaleRate,
      fringeRate: r.fringeRate,
    })) as Omit<JobClassificationRateRequestDto, 'jobId'>[],
  });

  const division: DivisionDto | undefined = useMemo(() => {
    return divisions.find((d) => d.id === values.division_id);
  }, [divisions, values.division_id]);

  const updateField = (field: string, value: any) => {
    setValues((prevState) => {
      return {
        ...prevState,
        [field]: value,
      };
    });
  };

  const updateJobDetails = async () => {
    setError(null);
    try {
      await updateDetails.mutateAsync({
        jobId: job.id,
        data: {
          name: values.name,
          description: values.description,
          jobNumber: values.job_number,
          customerName: values.customer_name,
          customerNumber: values.customer_number,
          location: values.location,
          divisionId: values.division_id,
          taxExempt: values.tax_exempt,
          vendorDiscounts: values.vendor_discounts,
          perDiemEnabled: values.per_diem_enabled,
          perDiemAmount: values.per_diem_amount,
          scalePayEnabled: values.scale_pay_enabled,
          classificationRates: values.classification_rates.map((rate) => ({
            classificationId: rate.classificationId,
            scaleRate: rate.scaleRate,
            fringeRate: rate.fringeRate,
          })),
          payCodeOverrides: values.pay_code_overrides.map((override) => ({
            payCodeId: override.payCodeId,
            rateModifierMultiplier: override.rateModifierMultiplier,
          })),
        },
      });
      onSave();
      onCancel();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const getType = (v?: string) => {
    if (!v) return 'N/A';
    const items = v.split('-');
    return items[1] ? items[1] : 'N/A';
  };

  const syncDetails = () => {
    if (isServiceJob) return;
    syncCtc.mutate(
      { jobId: job.id, jobNumber: values.job_number },
      {
        onSuccess: (result) =>
          setValues((prevState) => ({
            ...prevState,
            division: result.division,
          })),
      },
    );
  };

  return (
    <Stack gap="md">
      {!isServiceJob && (
        <BasicButton
          onClick={syncDetails}
          loading={syncCtc.isPending}
          variant="light"
          color="blue"
        >
          Sync CTC Details
        </BasicButton>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
          <Tabs.Tab value="paycodes">Pay Code Overrides</Tabs.Tab>
          <Tabs.Tab
            value="classifications"
            disabled={!values.scale_pay_enabled}
          >
            <Group gap="xs">
              <Text size="sm" c="black">
                Scale Pay Rates
              </Text>
              {values.scale_pay_enabled && (
                <Text size="xs" c="red">
                  *
                </Text>
              )}
            </Group>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic" pt="md">
          <Stack gap="md">
            <TextInput
              label={isServiceJob ? 'Service Job' : 'CTC Job'}
              required
              value={values.name}
              disabled
            />

            <ClientSelector
              initialValue={{
                custName: values.customer_name,
                custNumber: values.customer_number,
              }}
              onChange={(client) => {
                if (client) {
                  updateField('customer_name', client.custName);
                  updateField('customer_number', client.custNumber);
                }
              }}
            />

            <TextInput label="Division" value={division?.name || ''} disabled />

            <TextInput
              label="Job Type"
              value={isServiceJob ? 'Service' : getType(division.name)}
              disabled
            />

            <Textarea
              label="Scope of Work"
              placeholder="Enter a scope of work..."
              value={values.description}
              onChange={(e) => updateField('description', e.target.value)}
              autosize
              minRows={3}
            />

            <SimpleGrid cols={2}>
              <Radio.Group
                label="Tax Exempt"
                value={values.tax_exempt ? 'true' : 'false'}
                onChange={(value) =>
                  updateField('tax_exempt', value === 'true')
                }
              >
                <Group mt="xs">
                  <Radio value="true" label="Yes" />
                  <Radio value="false" label="No" />
                </Group>
              </Radio.Group>

              <Radio.Group
                label="Vendor Discounts"
                value={values.vendor_discounts ? 'true' : 'false'}
                onChange={(value) =>
                  updateField('vendor_discounts', value === 'true')
                }
              >
                <Group mt="xs">
                  <Radio value="true" label="Yes" />
                  <Radio value="false" label="No" />
                </Group>
              </Radio.Group>
            </SimpleGrid>

            <SimpleGrid cols={2}>
              <Radio.Group
                label="Scale Pay"
                value={values.scale_pay_enabled ? 'true' : 'false'}
                onChange={(value) => {
                  const enabled = value === 'true';
                  updateField('scale_pay_enabled', enabled);
                  if (!enabled) {
                    updateField('classification_rates', []);
                    if (activeTab === 'classifications') {
                      setActiveTab('basic');
                    }
                  }
                }}
              >
                <Group mt="xs">
                  <Radio value="true" label="Yes" />
                  <Radio value="false" label="No" />
                </Group>
              </Radio.Group>
              <Radio.Group
                label="Per Diem"
                value={values.per_diem_enabled ? 'true' : 'false'}
                onChange={(value) =>
                  updateField('per_diem_enabled', value === 'true')
                }
              >
                <Group mt="xs">
                  <Radio value="true" label="Yes" />
                  <Radio value="false" label="No" />
                </Group>
              </Radio.Group>
            </SimpleGrid>

            {values.per_diem_enabled && (
              <NumberInput
                label="Per Diem Amount"
                value={values.per_diem_amount}
                onChange={(value) => updateField('per_diem_amount', value)}
                min={0}
                decimalScale={2}
                prefix="$"
              />
            )}

            <PlaceSelector
              label="Jobsite Location"
              initialValue={values.location}
              onChange={(location) => updateField('location', location)}
            />

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
              >
                Job with job number {values.job_number} already exists
              </Alert>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="paycodes" pt="md">
          <PayCodeOverrides
            value={values.pay_code_overrides}
            onChange={(overrides) =>
              updateField('pay_code_overrides', overrides)
            }
          />
        </Tabs.Panel>

        <Tabs.Panel value="classifications" pt="md">
          <ClassificationRatesOverride
            value={values.classification_rates}
            onChange={(rates) => updateField('classification_rates', rates)}
          />
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" mt="md">
        <BasicButton variant="light" color="black" onClick={onCancel}>
          Cancel
        </BasicButton>
        <SaveButton
          onClick={updateJobDetails}
          disabled={values.job_number.length === 0}
          loading={updateDetails.isPending}
        />
      </Group>
    </Stack>
  );
};
